import { api } from './api';

export type InboxStatus = 'PENDING' | 'ANSWERED' | 'DISMISSED';
export type InboxStatusFilter = 'pending' | 'answered' | 'dismissed' | 'all';
export type InboxDismissReason =
  | 'NOT_A_QUESTION'
  | 'DUPLICATE'
  | 'RESOLVED_ELSEWHERE'
  | 'SPAM'
  | 'COMMENT_DELETED'
  | 'OTHER';
export type InboxSignature = 'TEAM' | 'SELF';

interface Titled {
  id: string;
  title: string;
}

/** Uma conversa da caixa: comentário raiz de aluno e o estado de triagem. */
export interface InboxItem {
  commentId: string;
  status: InboxStatus;
  dismissReason: InboxDismissReason | null;
  dismissNote: string | null;
  replyCount: number;
  reopenCount: number;
  excerpt: string;
  isDeleted: boolean;
  isEdited: boolean;
  student: { id: string; name: string; avatar: string | null } | null;
  lesson: Titled | null;
  module: Titled | null;
  product: (Titled & { slug: string }) | null;
  createdAt: string;
  lastActivityAt: string;
  lastStudentActivityAt: string;
  lastTeamActivityAt: string | null;
  answeredAt: string | null;
  dismissedAt: string | null;
}

export interface InboxMessage {
  id: string;
  content: string;
  contentFormat: 'TEXT' | 'MARKDOWN';
  authorKind: 'USER' | 'TEAM';
  isEdited: boolean;
  isDeleted?: boolean;
  createdAt: string;
  user: { id: string; name: string; avatar: string | null; role: string } | null;
  /** Quem publicou pela equipe; só a equipe vê. */
  publishedBy?: { id: string; name: string } | null;
}

export interface InboxThread {
  item: InboxItem;
  root: InboxMessage & { isPinned: boolean };
  replies: InboxMessage[];
}

export interface InboxSummary {
  pending: number;
  answered: number;
  dismissed: number;
  products: { id: string; title: string; pending: number; total: number }[];
}

export interface InboxFilters {
  status: InboxStatusFilter;
  productId?: string;
  search?: string;
  sort?: 'oldest' | 'newest';
}

export interface InboxPage {
  data: InboxItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export const commentInboxService = {
  async list(filters: InboxFilters, page: number, limit = 20): Promise<InboxPage> {
    const { data } = await api.get('/comments/inbox', {
      params: { ...filters, page, limit },
    });
    return { data: data.data, meta: data.meta };
  },

  async summary(): Promise<InboxSummary> {
    const { data } = await api.get('/comments/inbox/summary');
    return data?.data ?? data;
  },

  async thread(commentId: string): Promise<InboxThread> {
    const { data } = await api.get(`/comments/inbox/${commentId}`);
    return data?.data ?? data;
  },

  async reply(
    commentId: string,
    body: { content: string; signature: InboxSignature },
  ): Promise<InboxThread> {
    const { data } = await api.post(`/comments/inbox/${commentId}/reply`, body);
    return data?.data ?? data;
  },

  async dismiss(
    commentId: string,
    body: { reason: InboxDismissReason; note?: string },
  ): Promise<InboxItem> {
    const { data } = await api.post(`/comments/inbox/${commentId}/dismiss`, body);
    return data?.data ?? data;
  },

  async resolve(commentId: string): Promise<InboxItem> {
    const { data } = await api.post(`/comments/inbox/${commentId}/resolve`);
    return data?.data ?? data;
  },

  async reopen(commentId: string): Promise<InboxItem> {
    const { data } = await api.post(`/comments/inbox/${commentId}/reopen`);
    return data?.data ?? data;
  },
};
